import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Study } from '@models';
import {
  PortfolioLoadingService,
  PortfolioStatsService,
  SidebarService,
  StudyService
} from '@services';
import { Subscription } from 'rxjs';

type ConfirmationResult = 'yes' | 'no' | 'cancel';
type EmailProvider = 'gmail' | 'outlook' | 'default';
interface MailtoPayload {
  to: string;
  subject: string;
  body: string;
}

@Component({
  selector: 'home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  isLoading = false;
  sidebarCollapsed = true;
  private sub = new Subscription();
  private sidebar = inject(SidebarService);
  private studyService = inject(StudyService);
  private portfolioStatsService = inject(PortfolioStatsService);
  private portfolioLoadingService = inject(PortfolioLoadingService);
  private subscriptions = new Subscription();

  showModal = false;
  showProviderModal = false;
  emailPayload: MailtoPayload | null = null;

  onFeedbackOpenRequested(payload: MailtoPayload): void {
    this.emailPayload = {
      to: (payload.to ?? '').trim(),
      subject: payload.subject ?? '',
      body: payload.body ?? ''
    };
    this.showModal = true;
  }

  onModalClosed(result: ConfirmationResult): void {
    this.showModal = false;
    if (result === 'yes') {
      this.showProviderModal = true;
    }
  }

  onProviderSelected(provider: EmailProvider | 'cancel'): void {
    this.showProviderModal = false;
    if (!this.emailPayload || provider === 'cancel') return;

    const { to, subject, body } = this.emailPayload;
    const su = encodeURIComponent(subject);
    const bo = encodeURIComponent(body);
    const toParam = encodeURIComponent(to);

    let url = '';

    switch (provider) {
      case 'gmail':
        url = `https://mail.google.com/mail/u/0/?fs=1&tf=cm&to=${toParam}&su=${su}&body=${bo}`;
        break;
      case 'outlook':
        url = `https://outlook.office.com/mail/deeplink/compose?to=${toParam}&subject=${su}&body=${bo}`;
        break;
      case 'default':
        url = `mailto:${to}?subject=${su}&body=${bo}`;
        break;
    }

    window.open(url, '_blank', 'noopener');
  }

  ngOnInit(): void {
    this.sub.add(this.sidebar.sidebarCollapsed$.subscribe((v) => (this.sidebarCollapsed = v)));
    this.loadPortfolioData();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  private loadPortfolioData(): void {
    this.portfolioLoadingService.setLoading(true);
    const standardClientNames = this.studyService.getStandardClientNames();

    this.subscriptions.add(
      this.studyService.getStudiesPortfolio(standardClientNames).subscribe({
        next: (studies: Study[]) => {
          this.portfolioStatsService.calculatePortfolioStats(studies);
          this.portfolioLoadingService.setLoading(false);
        },
        error: (error) => {
          console.error('Error fetching portfolio data:', error);
          this.portfolioLoadingService.setLoading(false);
        }
      })
    );
  }
}
